import React, { useEffect, useState } from "react"
import { Box, Button } from "src/UILibrary"
import { useTranslation } from "react-i18next"
import InputDateField from "src/components/atom/InputDateField"
import SelectField from "src/components/atom/SelectField"
import CheckBoxGroupField from "src/components/atom/CheckBoxGroupField"
import InputTextField from "src/components/atom/InputTextField"
import dayjs, { Dayjs } from "dayjs"
import { GENDER, MEMBER_STATUS, PROVINCES, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { getCountriesForSelect } from "src/modules/helper"
import { useLoginContext } from "src/hooks/useLoginContext"
import { AddressService } from "src/openapi_address"
import { validateField, validateFields } from "src/modules/validation"
import * as yup from "yup"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import { CareerInputItems } from "src/pages/signup/adviser/component/CareerInputItems"
import FileSelectField from "src/components/atom/FileSelectField"
import InputNameField from "src/components/atom/InputNameField"
import { Adviser, Career, ProfileService } from "src/openapi"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import { ConfirmModal } from "src/UILibrary/ConfirmModal"
import { ShowProgress } from "src/states/showProgress"
import MultiSelectField from "src/components/atom/MultiSelectField"

type Props = {
  item: Adviser
}

export const AdviserDetail = ({ item }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const { jobs, areas, accessToken, checkLoginStatus } = useLoginContext()

  const [firstName, setFirstName] = useState<string>(item.firstName as string)
  const [lastName, setLastName] = useState<string>(item.lastName as string)
  const [firstNameJP, setFirstNameJP] = useState<string>(item.firstNameKana as string)
  const [lastNameJP, setLastNameJP] = useState<string>(item.lastNameKana as string)
  const [jobValues, setJobValues] = useState<number[]>([])
  const [otherJob, setOtherJob] = useState<string>(item.otherJobTitle as string)
  const [birthday, setBirthday] = useState<Dayjs | null>(dayjs(item.birthday as string))
  const [gender, setGender] = useState<string>(item.gender as string)
  const [nation, setNation] = useState<string>(item.country as string)
  const [areaId, setAreaId] = useState<number>(item.areaId as number)
  const [phoneNumber, setPhoneNumber] = useState<string>(item.phoneNumber as string)
  const [avatarPath, setAvatarPath] = useState<string>(item.photoUrl as string)
  const [postCode, setPostCode] = useState<string>(item.zipCode as string)
  const [province, setProvince] = useState<string>(item.province as string)
  const [city, setCity] = useState<string>(item.city as string)
  const [street, setStreet] = useState<string>(item.streetNo as string)
  const [building, setBuilding] = useState<string>(item.building as string)
  const [nameValidationMessage, setNameValidationMessage] = useState<string>("")
  const [jobValidationMessage, setJobValidationMessage] = useState<string>("")
  const [birthdayValidationMessage, setBirthdayValidationMessage] = useState<string>("")
  const [phoneNumberValidationMessage, setPhoneNumberValidationMessage] = useState<string>("")
  const [avatarPathValidationMessage, setAvatarPathValidationMessage] = useState<string>("")
  const [postCodeValidationMessage, setPostCodeValidationMessage] = useState<string>("")
  const [cityValidationMessage, setCityValidationMessage] = useState<string>("")

  const [abstract, setAbastrct] = useState<string>(item.pr as string)
  const [message, setMessage] = useState<string>(item.message as string)
  const [career, setCareer] = useState<Career[]>(item.career as Career[])
  const [abstractValidationMessage, setAbstractValidationMessage] = useState<string>("")
  const [messageValidationMessage, setMessageValidationMessage] = useState<string>("")
  const [careerValidationMessage, setCareerValidationMessage] = useState<string>("")

  const [lounge, setLounge] = useState<boolean>(item.isLoungeMember as boolean)
  const [accept, setAccept] = useState<string>(item.status as string)
  const [loungeValidationMessage, setLoungeValidationMessage] = useState<string>("")
  const [acceptValidationMessage, setAcceptValidationMessage] = useState<string>("")

  const [resident, setResident] = useState<boolean>(item.isResident as boolean)
  const [residentValidationMessage, setResidentValidationMessage] = useState<string>("")

  const [showConfirm, setShowConfirm] = useState<boolean>(false)

  useEffect(() => {
    let jobs: number[] = []
    item.jobTitleId?.split(",").map((id) => {
      jobs.push(parseInt(id))
    })
    setJobValues(jobs)
  }, [])

  const handlePostCodeInput = () => {
    if (!postCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(postCode).then((res) => {
      if (res.prefecture) {
        setProvince(res.prefecture as string)
      }

      if (res.city) {
        let city = res.city as string
        if (res.townCandidates && res.townCandidates.length >= 1) {
          city = `${city}${res.townCandidates[0]}`
        }
        setCity(city)
      }
    })
  }

  const validate = async () => {
    let validation = true

    try {
      await validateFields(
        yup.object().shape({
          firstName: yup.string().required(t("please_input_first_name")),
          lastName: yup.string().required(t("please_input_last_name")),
          firstNameJP: yup
            .string()
            .required(t("please_input_first_name_jp"))
            .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
          lastNameJP: yup
            .string()
            .required(t("please_input_last_name_jp"))
            .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
        }),
        ["firstName", "lastName", "firstNameJP", "lastNameJP"],
        [firstName, lastName, firstNameJP, lastNameJP]
      )
      validation = validation && true
      setNameValidationMessage("")
    } catch (err: any) {
      setNameValidationMessage(err.message)
      validation = validation && false
    }

    if (jobs.length === 0) {
      setJobValidationMessage(t("please_input_job"))
      validation = validation && false
    } else {
      setJobValidationMessage("")
    }

    try {
      await validateField(
        yup.object().shape({
          birthday: yup
            .string()
            .required(t("please_input_birthday"))
            .matches(/^(\d{4})\-(\d{1,2})\-(\d{1,2})$/, t("please_input_correct_birthday")),
        }),
        "birthday",
        birthday?.format("YYYY-MM-DD")
      )
      setBirthdayValidationMessage("")
    } catch (err: any) {
      setBirthdayValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          phoneNumber: yup
            .string()
            .required(t("please_input_phone_number"))
            .matches(
              /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
              t("please_input_correct_phone_number")
            ),
        }),
        "phoneNumber",
        phoneNumber
      )
      setPhoneNumberValidationMessage("")
    } catch (err: any) {
      setPhoneNumberValidationMessage(err.message)
      validation = validation && false
    }

    if (avatarPath) {
      setAvatarPathValidationMessage("")
    } else {
      setAvatarPathValidationMessage(t("please_select_file"))
    }

    try {
      await validateField(
        yup.object().shape({
          postCode: yup
            .string()
            .required(t("please_input_post_code"))
            .matches(/〒?[0-9]{3}-?[0-9]{4}/g, t("please_input_correct_post_code")),
        }),
        "postCode",
        postCode
      )
      setPostCodeValidationMessage("")
    } catch (err: any) {
      setPostCodeValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          city: yup.string().required(t("please_input_city")),
        }),
        "city",
        city
      )
      setCityValidationMessage("")
    } catch (err: any) {
      setCityValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          abstract: yup.string().required(t("please_input_abstract")),
        }),
        "abstract",
        abstract
      )
      setAbstractValidationMessage("")
    } catch (err: any) {
      setAbstractValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          message: yup.string().required(t("please_input_message")),
        }),
        "message",
        message
      )
      setMessageValidationMessage("")
    } catch (err: any) {
      setMessageValidationMessage(err.message)
      validation = validation && false
    }

    for (let i = 0; i < career.length; i++) {
      if (!career[i].career) {
        setCareerValidationMessage(t("please_input_correct_career"))
        validation = validation && false
        break
      }
      setCareerValidationMessage("")
    }

    if (lounge === undefined) {
      setLoungeValidationMessage(t("please_select_lounge"))
      validation = validation && false
    } else {
      setLoungeValidationMessage("")
    }

    if (accept === undefined) {
      setAcceptValidationMessage(t("please_select_status"))
      validation = validation && false
    } else {
      setAcceptValidationMessage("")
    }

    if (resident === undefined) {
      setResidentValidationMessage(t("please_select_resident"))
      validation = validation && false
    } else {
      setResidentValidationMessage("")
    }

    return validation
  }

  const handleUpdate = async () => {
    if (!(await validate())) {
      return
    }

    setShowProgress(true)
    ProfileService.updateAdviser(accessToken, item.userId as string, {
      photoUrl: avatarPath,
      firstName: firstName,
      lastName: lastName,
      firstNameKana: firstNameJP,
      lastNameKana: lastNameJP,
      jobTitleId: jobValues.join(","),
      otherJobTitle: otherJob,
      gender: gender,
      birthday: birthday?.format("YYYY-MM-DD") as string,
      country: nation,
      areaId: areaId,
      phoneNumber: phoneNumber,
      zipCode: postCode,
      province: province,
      city: city,
      streetNo: street,
      building: building,
      pr: abstract,
      message: message,
      career: career,
      isLoungeMember: lounge,
      status: accept,
      isResident: resident,
      comment: "ADVISER_UPDATE",
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success_update"), type: SNACKBAR_TYPES.SUCCESS })
        } else {
          setShowToast({ show: true, text: t("failed_update"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_update"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleRemove = () => {
    setShowConfirm(true)
  }

  const handleRemoveConfirm = () => {
    setShowProgress(true)
    ProfileService.deleteAdviser(accessToken, item.userId as string)
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success_remove"), type: SNACKBAR_TYPES.SUCCESS })
          location.reload()
        } else {
          setShowToast({ show: true, text: t("failed_remove"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_remove"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <Box sx={{ py: "40px", px: "20px" }}>
      <InputNameField
        name={t("name")}
        required
        firstName={firstName}
        lastName={lastName}
        firstNameJP={firstNameJP}
        lastNameJP={lastNameJP}
        handleFirstName={setFirstName}
        handleLastName={setLastName}
        handleFirstNameJP={setFirstNameJP}
        handleLastNameJP={setLastNameJP}
        validationMessage={nameValidationMessage}
      ></InputNameField>
      <MultiSelectField
        name={t("adviser_position")}
        required
        sx={{ mt: "40px", width: "200px" }}
        inputStyle={{ width: "400px" }}
        datas={jobs}
        selected={jobValues}
        handleSelected={setJobValues}
        hasText
        text={otherJob}
        handleText={setOtherJob}
        validationMessage={jobValidationMessage}
      ></MultiSelectField>
      <InputDateField
        name={t("birthday")}
        required
        sx={{ mt: "30px" }}
        date={birthday}
        handleDate={setBirthday}
        validationMessage={birthdayValidationMessage}
      ></InputDateField>
      <SelectField
        name={t("sex")}
        required
        sx={{ mt: "30px", width: "355px" }}
        selected={gender}
        handleSelected={setGender}
        datas={[
          { label: t("male"), value: GENDER.MALE },
          { label: t("female"), value: GENDER.FEMALE },
        ]}
      ></SelectField>
      <SelectField
        name={t("nation")}
        required
        sx={{ mt: "30px", width: "355px" }}
        selected={nation}
        handleSelected={setNation}
        datas={getCountriesForSelect()}
      ></SelectField>
      <InputTextField
        name={t("phone_number")}
        required
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={phoneNumber}
        handleText={setPhoneNumber}
        validationMessage={phoneNumberValidationMessage}
        maxLength={15}
      ></InputTextField>
      <SelectField
        name={t("address")}
        required
        sx={{ mt: "40px", mb: "40px", width: "200px" }}
        selected={areaId}
        handleSelected={setAreaId}
        datas={areas}
      ></SelectField>
      <FileSelectField
        required
        name={t("profile_photo")}
        handleChange={setAvatarPath}
        file={avatarPath}
        validationMessage={avatarPathValidationMessage}
      />
      <InputTextField
        name={t("post_code")}
        required
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={postCode}
        handleText={setPostCode}
        validationMessage={postCodeValidationMessage}
        handleFocusOut={handlePostCodeInput}
        maxLength={10}
      ></InputTextField>
      <SelectField
        name={t("province")}
        required
        sx={{ mt: "30px", width: "200px" }}
        selected={province}
        handleSelected={setProvince}
        datas={PROVINCES}
      ></SelectField>
      <InputTextField
        name={t("city")}
        required
        sx={{ mt: "30px" }}
        text={city}
        handleText={setCity}
        validationMessage={cityValidationMessage}
        maxLength={50}
      ></InputTextField>
      <InputTextField
        name={t("street")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
        text={street}
        handleText={setStreet}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("building")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "690px" }}
        text={building}
        handleText={setBuilding}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("history")}
        placeholder={t("history_placeholder")}
        multiline
        required
        inputStyle={{
          width: "690px",
        }}
        text={abstract}
        handleText={setAbastrct}
        validationMessage={abstractValidationMessage}
        maxLength={400}
      />
      <InputTextField
        name={t("message")}
        placeholder={t("message_placeholder")}
        multiline
        required
        sx={{ mt: "30px" }}
        inputStyle={{ width: "690px" }}
        text={message}
        handleText={setMessage}
        validationMessage={messageValidationMessage}
        maxLength={400}
      />
      <CareerInputItems
        required
        sx={{ mt: "80px" }}
        career={career}
        handleCareer={setCareer}
        validationMessage={careerValidationMessage}
      />
      <CheckBoxGroupField
        required
        radio
        name={t("resident_setting")}
        datas={[
          {
            label: t("no"),
            value: false,
          },
          {
            label: t("yes"),
            value: true,
          },
        ]}
        selected={resident}
        handleSelected={setResident}
        sx={{ mt: "30px" }}
        validationMessage={residentValidationMessage}
      />
      <CheckBoxGroupField
        required
        radio
        name={t("is_lounge")}
        datas={[
          {
            label: t("lounge"),
            value: true,
          },
          {
            label: t("not_lounge"),
            value: false,
          },
        ]}
        selected={lounge}
        handleSelected={setLounge}
        sx={{ mt: "30px" }}
        validationMessage={loungeValidationMessage}
      />
      <CheckBoxGroupField
        required
        radio
        name={t("member_status")}
        datas={[
          { label: t("requested"), value: MEMBER_STATUS.REQUEST },
          { label: t("accepted"), value: MEMBER_STATUS.ACCEPTED },
        ]}
        selected={accept}
        handleSelected={setAccept}
        sx={{ mt: "30px" }}
        validationMessage={acceptValidationMessage}
      />
      <Box display="flex" sx={{ mt: "30px" }}>
        <OutlinedButton
          caption={t("delete")}
          fontColor="#FC3317"
          fontWeight="700"
          fontSize={16}
          sx={{
            borderColor: "#FC3317",
            minWidth: "240px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: "auto",
            mr: "40px",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
          }}
          onClick={() => handleRemove()}
        />
        <Button
          variant="contained"
          sx={{
            minWidth: "240px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: 0,
            mr: "auto",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
            ":hover": {
              bgcolor: "#2DA389",
            },
          }}
          onClick={() => handleUpdate()}
        >
          {t("update")}
        </Button>
      </Box>
      <ConfirmModal
        open={showConfirm}
        handleOpen={setShowConfirm}
        text={t("do_you_want_to_remove")}
        confirmText={t("remove")}
        cancelText={t("cancel")}
        handleConfirm={handleRemoveConfirm}
        confirmStyle={{ color: "#FC3317", borderColor: "#FC3317" }}
      />
    </Box>
  )
}
